/**
 * Best-effort parsing of a free-typed ingredient line into structured form, for
 * **manual entry/edit only** — recipes imported by the model arrive already
 * structured. The rule is deliberately forgiving and null-safe: a line it cannot
 * split becomes a name with no quantity, which scales as "leave unchanged" rather
 * than guessing.
 *
 *   "500 g spaghetti"     → { quantity: 500, unit: 'g',  name: 'spaghetti' }
 *   "2 teentjes knoflook" → { quantity: 2,   unit: 'teentjes', name: 'knoflook' }
 *   "1/2 ui"              → { quantity: 0.5, unit: null, name: 'ui' }
 *   "snufje zout"         → { quantity: null, unit: null, name: 'snufje zout' }
 */
/** Dutch cooking units, so a unit token is not swallowed into the name. */
export const UNITS = new Set([
    'g', 'gr', 'gram', 'grammen',
    'kg', 'kilo', 'kilogram',
    'mg',
    'ml', 'cl', 'dl', 'l', 'liter', 'liter',
    'el', 'eetlepel', 'eetlepels',
    'tl', 'theelepel', 'theelepels',
    'kop', 'koppen', 'mok',
    'snuf', 'snufje', 'mespunt',
    'teen', 'teentje', 'teentjes',
    'blik', 'blikje', 'blikjes',
    'pak', 'pakje', 'pakjes',
    'zak', 'zakje',
    'bos', 'bosje', 'bossen',
    'stuk', 'stuks',
    'plak', 'plakken', 'plakjes',
    'scheut', 'scheutje',
    'klont', 'klontje', 'klontjes',
    'hand', 'handje', 'handvol',
    'takje', 'takjes',
    'blaadje', 'blaadjes',
]);
export const UNICODE_FRACTIONS = {
    '½': 0.5, '¼': 0.25, '¾': 0.75, '⅓': 1 / 3, '⅔': 2 / 3, '⅕': 0.2, '⅛': 0.125,
};
/** Reads a leading quantity token: decimal (`1,5`/`1.5`), simple fraction (`1/2`), or a unicode fraction. Returns `[value, restIndex]` or null. */
function readQuantity(s) {
    const first = s[0];
    if (first && first in UNICODE_FRACTIONS)
        return [UNICODE_FRACTIONS[first], 1];
    const frac = /^(\d+)\s*\/\s*(\d+)/.exec(s);
    if (frac) {
        const n = Number(frac[1]);
        const d = Number(frac[2]);
        if (d !== 0)
            return [n / d, frac[0].length];
    }
    const dec = /^(\d+(?:[.,]\d+)?)/.exec(s);
    if (dec)
        return [Number(dec[1].replace(',', '.')), dec[0].length];
    return null;
}
export function parseIngredientLine(line) {
    const trimmed = line.trim();
    if (!trimmed)
        return { quantity: null, unit: null, name: '' };
    const q = readQuantity(trimmed);
    if (!q)
        return { quantity: null, unit: null, name: trimmed };
    const [quantity, at] = q;
    let rest = trimmed.slice(at).trim();
    let unit = null;
    const firstWord = /^(\S+)/.exec(rest)?.[1];
    if (firstWord && UNITS.has(firstWord.toLowerCase())) {
        unit = firstWord.toLowerCase();
        rest = rest.slice(firstWord.length).trim();
    }
    return { quantity, unit, name: rest || trimmed };
}
/** Renders a numeric amount for display: whole numbers plain, else Dutch-decimal (comma), trailing zeros trimmed. */
export function formatQuantity(n) {
    if (Number.isInteger(n))
        return String(n);
    // Round to 2 decimals, drop trailing zeros, comma separator.
    const rounded = Math.round(n * 100) / 100;
    return String(rounded).replace('.', ',');
}
/** Renders an ingredient back to a single line, optionally overriding the amount (for a scaled view). */
export function formatIngredient(ing, quantityOverride) {
    const q = quantityOverride === undefined ? ing.quantity : quantityOverride;
    const parts = [];
    if (q != null)
        parts.push(formatQuantity(q));
    if (ing.unit)
        parts.push(ing.unit);
    parts.push(ing.name);
    const base = parts.join(' ');
    return ing.note ? `${base} (${ing.note})` : base;
}
//# sourceMappingURL=ingredient-parse.js.map